import React from "react";
import { Switch, FormControlLabel, TextField, MenuItem, Divider } from "@material-ui/core";
import { CTextField, CSelect, HFlex, Text, MultiSelect } from "shared";
import { Controller, useFormContext } from "react-hook-form";
import { useHcpProfessionals } from "api/hooks/useHcpServices";
import { ProfessionalItem, ServiceItem } from "routes/hcp/types";

export interface ServiceFormData {
  health_care_professional_ids: number[];
  name: string;
  description: string;
  duration: string;
  mode: string;
  purpose: string;
  payment_methods: string[];
  opening_hours: string;
  closing_hours: string;
  no_earlier_than_value: string;
  no_earlier_than_unit: string;
  no_later_than_value: string;
  no_later_than_unit: string;
  capacity: string;
  health_questionnaire_type: string;
  bookable_by_patients: boolean;
}

const durationOptions = [
  { value: "15", label: "15 min" },
  { value: "20", label: "20 min" },
  { value: "30", label: "30 min" },
  { value: "45", label: "45 min" },
];

const modeOptions = [
  { value: "physical", label: "Physical" },
  { value: "digital", label: "Digital" },
];

const purposeOptions = [
  { value: "kick_off", label: "Kick Off" },
  { value: "follow_up", label: "Follow Up" },
  { value: "general", label: "General" },
  { value: "discharge_call", label: "Discharge Call" },
];

const paymentMethodOptions = [
  { value: "bank_card", label: "Bank Card" },
  { value: "on_site", label: "On Site" },
  { value: "exemption_card", label: "Exemption Card" },
  { value: "free", label: "Free" },
];

const hourOptions = Array.from({ length: 24 }, (_, i) => ({
  value: String(i * 60),
  label: `${String(i).padStart(2, "0")}:00`,
}));

const timeUnitOptions = [
  { value: "minutes", label: "Minutes" },
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
  { value: "weeks", label: "Weeks" },
];

const questionnaireOptions = [
  { value: "none", label: "None" },
  { value: "default", label: "Default" },
];

export const toMinutes = (value: string, unit: string): number | undefined => {
  const num = Number(value);
  if (!value || isNaN(num)) return undefined;
  switch (unit) {
    case "hours":
      return num * 60;
    case "days":
      return num * 60 * 24;
    case "weeks":
      return num * 60 * 24 * 7;
    default:
      return num;
  }
};

export const fromMinutes = (minutes: number | undefined, fallback: { value: string; unit: string }) => {
  if (minutes == null) return fallback;
  if (minutes > 0 && minutes % (60 * 24 * 7) === 0) return { value: String(minutes / (60 * 24 * 7)), unit: "weeks" };
  if (minutes > 0 && minutes % (60 * 24) === 0) return { value: String(minutes / (60 * 24)), unit: "days" };
  if (minutes > 0 && minutes % 60 === 0) return { value: String(minutes / 60), unit: "hours" };
  return { value: String(minutes), unit: "minutes" };
};

export const serviceToFormValues = (service: ServiceItem): ServiceFormData => {
  const noEarlierThan = fromMinutes(service.no_earlier_than, { value: "", unit: "minutes" });
  const noLaterThan = fromMinutes(service.no_later_than, { value: "", unit: "weeks" });

  return {
    name: service.name,
    description: service.description ?? "",
    duration: String(service.duration),
    mode: service.mode,
    purpose: service.purpose,
    payment_methods: service.payment_methods ?? [],
    opening_hours: service.opening_hours != null ? String(service.opening_hours) : String(7 * 60),
    closing_hours: service.closing_hours != null ? String(service.closing_hours) : String(22 * 60),
    no_earlier_than_value: noEarlierThan.value,
    no_earlier_than_unit: noEarlierThan.unit,
    no_later_than_value: noLaterThan.value,
    no_later_than_unit: noLaterThan.unit,
    capacity: service.capacity != null ? String(service.capacity) : "",
    health_questionnaire_type: service.health_questionnaire_type ?? "none",
    bookable_by_patients: service.bookable_by_patients,
    health_care_professional_ids: service.health_care_professional_ids ?? [],
  };
};

export const formValuesToPayload = (fields: ServiceFormData) => ({
  name: fields.name,
  description: fields.description,
  duration: Number(fields.duration),
  mode: fields.mode,
  purpose: fields.purpose,
  payment_methods: fields.payment_methods.length > 0 ? fields.payment_methods : undefined,
  opening_hours: fields.opening_hours ? Number(fields.opening_hours) : undefined,
  closing_hours: fields.closing_hours ? Number(fields.closing_hours) : undefined,
  no_earlier_than: toMinutes(fields.no_earlier_than_value, fields.no_earlier_than_unit),
  no_later_than: toMinutes(fields.no_later_than_value, fields.no_later_than_unit),
  capacity: fields.capacity ? Number(fields.capacity) : undefined,
  health_questionnaire_type: fields.health_questionnaire_type || undefined,
  bookable_by_patients: fields.bookable_by_patients,
  health_care_professional_ids: fields.health_care_professional_ids,
});

const professionalLabel = (professional: ProfessionalItem) =>
  professional.name ||
  [professional.first_name, professional.last_name].filter(Boolean).join(" ") ||
  professional.email ||
  `Professional #${professional.id}`;

interface Props {
  hcpId: number;
  paymentMethodsDefault: string[];
  professionalIdsDefault?: number[];
}

const ServiceFormFields: React.VFC<Props> = ({ hcpId, paymentMethodsDefault, professionalIdsDefault = [] }) => {
  const { control } = useFormContext<ServiceFormData>();

  const { data: professionals, isLoading: isLoadingProfessionals } = useHcpProfessionals(hcpId);
  const professionalOptions = (professionals ?? []).map(p => ({ value: p.id, label: professionalLabel(p) }));

  return (
    <>
      <CTextField required name="name" autoFocus label="Name" margin="normal" size="small" fullWidth />
      <CTextField required name="description" label="Description" margin="normal" size="small" fullWidth />

      <HFlex gap={2}>
        <CSelect name="duration" label="Duration" items={durationOptions} margin="normal" size="small" fullWidth>
          {({ label }) => <Text>{label}</Text>}
        </CSelect>
        <CSelect name="mode" label="Mode" items={modeOptions} margin="normal" size="small" fullWidth>
          {({ label }) => <Text>{label}</Text>}
        </CSelect>
        <CSelect name="purpose" label="Purpose" items={purposeOptions} margin="normal" size="small" fullWidth>
          {({ label }) => <Text>{label}</Text>}
        </CSelect>
      </HFlex>

      <Divider style={{ margin: "16px 0 8px" }} />

      <Text bold mb={1}>
        Scheduling
      </Text>

      <MultiSelect
        name="payment_methods"
        label="Payment Methods"
        options={paymentMethodOptions}
        defaultValue={paymentMethodsDefault}
        margin="normal"
      />

      <HFlex gap={2}>
        <CSelect name="opening_hours" label="Opening Hours" items={hourOptions} margin="normal" size="small" fullWidth>
          {({ label }) => <Text>{label}</Text>}
        </CSelect>
        <CSelect name="closing_hours" label="Closing Hours" items={hourOptions} margin="normal" size="small" fullWidth>
          {({ label }) => <Text>{label}</Text>}
        </CSelect>
      </HFlex>

      <HFlex gap={2} alignItems="flex-end">
        <CTextField
          name="no_earlier_than_value"
          label="No Earlier Than"
          asNumber
          margin="normal"
          size="small"
          style={{ flex: 1 }}
        />
        <Controller
          name="no_earlier_than_unit"
          control={control}
          render={({ value, onChange }) => (
            <TextField
              select
              value={value}
              onChange={e => onChange(e.target.value)}
              margin="normal"
              size="small"
              style={{ minWidth: 120 }}
            >
              {timeUnitOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <CTextField
          name="no_later_than_value"
          label="No Later Than"
          asNumber
          margin="normal"
          size="small"
          style={{ flex: 1 }}
        />
        <Controller
          name="no_later_than_unit"
          control={control}
          render={({ value, onChange }) => (
            <TextField
              select
              value={value}
              onChange={e => onChange(e.target.value)}
              margin="normal"
              size="small"
              style={{ minWidth: 120 }}
            >
              {timeUnitOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </HFlex>

      <HFlex gap={2}>
        <CTextField name="capacity" label="Capacity" asNumber margin="normal" size="small" fullWidth />
        <CSelect
          name="health_questionnaire_type"
          label="Health Questionnaire"
          items={questionnaireOptions}
          margin="normal"
          size="small"
          fullWidth
        >
          {({ label }) => <Text>{label}</Text>}
        </CSelect>
      </HFlex>

      <Controller
        name="bookable_by_patients"
        control={control}
        render={({ value, onChange }) => (
          <FormControlLabel
            control={<Switch checked={value} onChange={e => onChange(e.target.checked)} color="primary" />}
            label="Bookable by Patients"
          />
        )}
      />

      <Divider style={{ margin: "16px 0 8px" }} />

      <Text bold mb={1}>
        Professionals
      </Text>

      <MultiSelect
        name="health_care_professional_ids"
        label="Professionals"
        options={professionalOptions}
        defaultValue={professionalIdsDefault}
        loading={isLoadingProfessionals}
        noOptionsText="No professionals found"
        margin="normal"
      />
    </>
  );
};

export default ServiceFormFields;
